/**
 * StatusChip — Semantic status badge with consistent colours.
 *
 * `dot` renders a small colored circle in front of the label instead of a
 * flat-filled pill — the "quiet dot + label" treatment Linear/Vercel/GitHub
 * use for a healthy/at-a-glance status (this codebase already had that exact
 * pattern in connectorUi.tsx's StatusPill; this generalizes it onto the one
 * status component every other list page already shares, instead of a second
 * one-off implementation).
 */
import { Box, Chip, ChipProps } from '@mui/material';
import React from 'react';

type Status =
  | 'active'
  | 'inactive'
  | 'enabled'
  | 'disabled'
  | 'new'
  | 'contacted'
  | 'qualified'
  | 'unqualified'
  | 'converted'
  | 'lost'
  | 'archived'
  | 'draft'
  | 'paused'
  | 'completed'
  | 'resolved'
  | 'spam'
  | 'success'
  | 'error'
  | 'warning'
  | 'info'
  | string;

const palette: Record<string, { bg: string; color: string }> = {
  active: { bg: '#dcfce7', color: '#15803d' },
  enabled: { bg: '#dcfce7', color: '#15803d' },
  success: { bg: '#dcfce7', color: '#15803d' },
  converted: { bg: '#dcfce7', color: '#15803d' },
  completed: { bg: '#dcfce7', color: '#15803d' },

  inactive: { bg: '#f1f5f9', color: '#64748b' },
  disabled: { bg: '#f1f5f9', color: '#64748b' },
  draft: { bg: '#f1f5f9', color: '#64748b' },
  archived: { bg: '#f1f5f9', color: '#64748b' },
  // Terminal disqualification — a darker slate than 'archived' so the two neutral
  // states stay visually distinct, and clearly not red like 'lost' (a lost deal).
  unqualified: { bg: '#e2e8f0', color: '#475569' },

  new: { bg: '#ede9fe', color: '#6d28d9' },
  info: { bg: '#dbeafe', color: '#1d4ed8' },
  qualified: { bg: '#dbeafe', color: '#1d4ed8' },
  confirmed: { bg: '#dbeafe', color: '#1d4ed8' },
  resolved: { bg: '#dbeafe', color: '#1d4ed8' },

  contacted: { bg: '#fef3c7', color: '#92400e' },
  paused: { bg: '#fef3c7', color: '#92400e' },
  warning: { bg: '#fef3c7', color: '#92400e' },
  requested: { bg: '#fef3c7', color: '#92400e' },

  lost: { bg: '#fee2e2', color: '#b91c1c' },
  error: { bg: '#fee2e2', color: '#b91c1c' },
  cancelled: { bg: '#fee2e2', color: '#b91c1c' },
  no_show: { bg: '#fee2e2', color: '#b91c1c' },
  spam: { bg: '#fee2e2', color: '#b91c1c' },
};

interface StatusChipProps extends Omit<ChipProps, 'color'> {
  status: Status;
  /** Quiet dot + label instead of a flat-filled pill. */
  dot?: boolean;
}

const StatusChip: React.FC<StatusChipProps> = ({ status, label, size = 'small', dot, ...rest }) => {
  const key = status?.toLowerCase();
  const colors = palette[key] ?? { bg: '#f1f5f9', color: '#64748b' };

  return (
    <Chip
      label={label ?? status}
      size={size}
      {...(dot
        ? {
            icon: (
              <Box
                sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: colors.color, flexShrink: 0 }}
              />
            ),
          }
        : {})}
      {...rest}
      sx={{
        bgcolor: colors.bg,
        color: colors.color,
        fontWeight: 600,
        fontSize: '0.6875rem',
        border: 'none',
        height: size === 'small' ? 22 : 26,
        borderRadius: 5,
        '& .MuiChip-label': { px: 1.25 },
        ...(dot ? { '& .MuiChip-icon': { ml: 1, mr: -0.25 } } : {}),
        ...rest.sx,
      }}
    />
  );
};

export default StatusChip;
